/**
 * Document head management.
 *
 * The site is a client-rendered SPA, so every tag here is written after the page data
 * arrives. That is fine for canonical and hreflang - crawlers that matter execute
 * JavaScript - but it is worth being honest that server rendering would be better, and
 * is the right answer if organic search ever becomes a priority. Noted in the SEO
 * section of docs/milestones.md.
 *
 * Every tag written here is marked `data-weco-head` so the next page can clear exactly
 * what the previous one added, and nothing else.
 */

const MARK = 'data-weco-head';

function clear() {
  document.head.querySelectorAll(`[${MARK}]`).forEach((el) => el.remove());
}

function add(tag: 'link' | 'meta', attrs: Record<string, string>) {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) el.setAttribute(key, value);
  el.setAttribute(MARK, '');
  document.head.appendChild(el);
}

export interface HeadOptions {
  title: string;
  description?: string | null;
  /** Absolute URL of this page on its own regional domain. */
  canonical?: string;
  /** `{ locale, url }` for the same page in the region's other languages. */
  alternates?: { locale: string; url: string }[];
  ogImage?: string;
  noindex?: boolean;
}

/**
 * Called after every head write.
 *
 * Analytics listens here rather than to the router: a page view sent on the URL change
 * would carry the previous page's title, because the title is only known once the CMS
 * has answered. This fires at the moment the page is actually the page it claims to be.
 */
type HeadListener = (info: { title: string; noindex: boolean }) => void;
const headListeners = new Set<HeadListener>();

export function onHeadApplied(listener: HeadListener): () => void {
  headListeners.add(listener);
  return () => { headListeners.delete(listener); };
}

export function applyHead({ title, description, canonical, alternates, ogImage, noindex }: HeadOptions) {
  document.title = title;
  clear();

  if (description) add('meta', { name: 'description', content: description });
  if (noindex) add('meta', { name: 'robots', content: 'noindex, nofollow' });
  if (canonical) add('link', { rel: 'canonical', href: canonical });

  for (const alt of alternates ?? []) {
    add('link', { rel: 'alternate', hreflang: alt.locale, href: alt.url });
  }

  add('meta', { property: 'og:title', content: title });
  add('meta', { property: 'og:type', content: 'website' });
  if (description) add('meta', { property: 'og:description', content: description });
  if (canonical) add('meta', { property: 'og:url', content: canonical });
  if (ogImage) add('meta', { property: 'og:image', content: ogImage });

  for (const listener of headListeners) listener({ title, noindex: Boolean(noindex) });
}

/**
 * The host a market's canonical addresses should name, given the host being served.
 *
 * `region.domain` is the apex (`wecotravel.pl`) and is what a request is matched to a market
 * by. But a market can be served from `www.` of it - Poland is, and the gateway answers the
 * apex with a 301 to `www.wecotravel.pl` - and a canonical that names a URL which redirects
 * is the page telling a crawler its preferred address is not an address. So on
 * `www.<domain>` the canonical is that host.
 *
 * On anything else - the apex, `beta.*`, a test host, localhost - it is the region's own
 * domain, as it always was: in development the domain does not resolve to anything, which
 * is exactly right, because a canonical URL must name the production address, not
 * localhost, and a `beta.` page must not canonicalise to itself and compete with the live
 * one. The CMS makes the same call for `sitemap.xml` and `robots.txt` - `servedOrigin` in
 * apps/cms/src/api/website/services/seo.js - and the two have to agree, or a sitemap lists
 * one host while the pages it lists name another.
 */
export function canonicalHost(domain: string, currentHost: string): string {
  const own = String(domain || '').toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
  const bare = own.replace(/^www\./, '');
  const current = String(currentHost || '').toLowerCase().split(':')[0].replace(/\.$/, '');
  return bare && current === `www.${bare}` ? current : own;
}

/**
 * The absolute URL of a path on a region's canonical host - see `canonicalHost`.
 *
 * `currentHost` is a parameter so the rule can be run without a browser; it defaults to the
 * page's own, which is what every caller wants.
 */
export function absoluteUrl(
  domain: string,
  path: string,
  currentHost: string = typeof window === 'undefined' ? '' : window.location.hostname
): string {
  const host = canonicalHost(domain, currentHost);
  if (!host) return '';
  return `https://${host}${path.startsWith('/') ? path : `/${path}`}`;
}
